import React, { useEffect, useMemo, useState } from 'react'
import { X, TrendingUp, TrendingDown, Minus, AlertTriangle, Target, Award, Activity, Star, Calendar, BarChart3 } from 'lucide-react'
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend
} from 'recharts'
import { useGymnastAnalysis } from '../../hooks/useGymnastAnalysis'
import { supabase } from '../../config/supabase'

const APPARATUS_LABELS = {
  salto: 'Salto', viga: 'Viga', suelo: 'Suelo',
  barras_asimetricas: 'B. Asimétricas', barras_paralelas: 'B. Paralelas',
  barra_fija: 'B. Fija', caballo: 'Caballo', anillas: 'Anillas',
  aro: 'Aro', pelota: 'Pelota', mazas: 'Mazas', cinta: 'Cinta',
  vt: 'Salto', ub: 'B. Asimétricas', bb: 'Viga', fx: 'Suelo',
  ph: 'Caballo', sr: 'Anillas', pb: 'B. Paralelas', hb: 'B. Fija',
  hoop: 'Aro', ball: 'Pelota', clubs: 'Mazas', ribbon: 'Cinta',
  all_around: 'All Around'
}
const aLabel = (k) => APPARATUS_LABELS[k] || k

const GymnastAnalysisModal = ({ gymnast, onClose }) => {
  const { analysis, loading, error, refetch } = useGymnastAnalysis(gymnast?.id)
  const [historicScores, setHistoricScores] = useState([])
  const [scoresLoading, setScoresLoading] = useState(false)

  useEffect(() => {
    if (!gymnast?.id) return
    let cancelled = false
    const load = async () => {
      setScoresLoading(true)
      try {
        const { data, error: err } = await supabase
          .from('scores')
          .select('id, event_id, gymnast_id, scores, total, average, created_at, events:event_id(id, name, date)')
          .eq('gymnast_id', gymnast.id)
          .order('created_at', { ascending: true })
        if (!cancelled && !err) setHistoricScores(data || [])
      } catch (e) {
        console.error('[GymnastAnalysisModal] scores fetch error', e)
      } finally {
        if (!cancelled) setScoresLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [gymnast?.id])

  // Línea temporal por copa (avg total y puntajes por aparato)
  const temporalSeries = useMemo(() => {
    if (!historicScores.length) return []
    return historicScores.map(s => {
      const dateStr = s.events?.date || s.created_at
      return {
        id: s.id,
        name: s.events?.name || 'Copa',
        date: dateStr,
        dateLabel: dateStr ? new Date(dateStr).toLocaleDateString('es', { day: 'numeric', month: 'short', year: '2-digit' }) : '—',
        average: typeof s.average === 'number' ? parseFloat(s.average.toFixed(2)) : 0,
        total: typeof s.total === 'number' ? parseFloat(s.total.toFixed(2)) : 0,
        scores: s.scores || {},
      }
    }).sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0))
  }, [historicScores])

  // Promedio y mejor puntaje por aparato (agregado histórico)
  const apparatusAgg = useMemo(() => {
    const map = {}
    historicScores.forEach(s => {
      Object.entries(s.scores || {}).forEach(([app, val]) => {
        const v = Number(val)
        if (!v || v <= 0) return
        if (!map[app]) map[app] = []
        map[app].push(v)
      })
    })
    return Object.entries(map).map(([app, vals]) => ({
      apparatus: aLabel(app),
      avg: parseFloat((vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2)),
      max: parseFloat(Math.max(...vals).toFixed(2)),
      min: parseFloat(Math.min(...vals).toFixed(2)),
      count: vals.length,
    })).sort((a, b) => b.avg - a.avg)
  }, [historicScores])

  // Evolución por aparato (line chart multi-serie)
  const apparatusEvolution = useMemo(() => {
    if (!temporalSeries.length) return { data: [], apparatusKeys: [] }
    const keySet = new Set()
    temporalSeries.forEach(s => Object.keys(s.scores || {}).forEach(k => {
      const v = Number(s.scores[k])
      if (v > 0) keySet.add(k)
    }))
    const keys = Array.from(keySet)
    const data = temporalSeries.map(s => {
      const row = { dateLabel: s.dateLabel, copa: s.name }
      keys.forEach(k => {
        const v = Number(s.scores?.[k])
        if (v > 0) row[aLabel(k)] = parseFloat(v.toFixed(2))
      })
      return row
    })
    return { data, apparatusKeys: keys.map(aLabel) }
  }, [temporalSeries])

  const CHART_COLORS = ['#8b5cf6', '#10b981', '#f59e0b', '#3b82f6', '#ef4444', '#ec4899', '#14b8a6', '#6366f1']

  const getTrendIcon = (value) => {
    if (value > 0) return <TrendingUp size={14} className="trend-up" />
    if (value < 0) return <TrendingDown size={14} className="trend-down" />
    return <Minus size={14} className="trend-neutral" />
  }

  const getTrendClass = (value) => {
    if (value > 0) return 'trend-up'
    if (value < 0) return 'trend-down'
    return 'trend-neutral'
  }

  return (
    <div className="cd-modal-overlay" onClick={onClose}>
      <div className="cd-modal-content cd-modal-large" onClick={e => e.stopPropagation()}>
        <div
          className="cd-modal-header"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 18px', borderBottom: '1px solid #e4e4e7',
            position: 'sticky', top: 0, background: 'white', zIndex: 5, borderRadius: '12px 12px 0 0'
          }}
        >
          <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, color: '#18181b' }}>
            <Activity size={20} style={{ color: '#8b5cf6' }} /> Análisis de Rendimiento
          </h2>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 34, height: 34, borderRadius: 8, border: '1px solid #e4e4e7',
              background: '#fafafa', color: '#52525b', cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.borderColor = '#fecaca'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#fafafa'; e.currentTarget.style.color = '#52525b'; e.currentTarget.style.borderColor = '#e4e4e7'; }}
          >
            <X size={18} />
          </button>
        </div>

        <div className="cd-modal-body">
          {/* Header con datos básicos */}
          <div className="cd-analysis-header" style={{ marginBottom: 14 }}>
            <div className="cd-analysis-avatar">{gymnast?.name?.[0]?.toUpperCase() || 'A'}</div>
            <div className="cd-analysis-info">
              <h3>{gymnast?.name || 'Atleta'}</h3>
              <p>{gymnast?.level || 'Sin nivel'} • {gymnast?.discipline || '—'}</p>
              {!scoresLoading && (
                <span className="cd-analysis-years">
                  {historicScores.length} copa{historicScores.length !== 1 ? 's' : ''} registrada{historicScores.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            {analysis && (
              <div className={`cd-growth-badge ${getTrendClass(analysis.growth_since_registration)}`}>
                {getTrendIcon(analysis.growth_since_registration)}
                <span>{analysis.growth_since_registration > 0 ? '+' : ''}{analysis.growth_since_registration.toFixed(1)}%</span>
              </div>
            )}
          </div>

          {/* ===== Gráficos (siempre que haya al menos 1 copa) ===== */}
          {scoresLoading && (
            <div className="cd-loading-state" style={{ padding: 20 }}>
              <div className="cd-spinner" />
              <p>Cargando historial de copas...</p>
            </div>
          )}

          {!scoresLoading && historicScores.length === 0 && (
            <div className="cd-empty-state" style={{ padding: 24, textAlign: 'center' }}>
              <BarChart3 size={40} style={{ opacity: 0.4, margin: '0 auto 8px' }} />
              <p style={{ margin: 0, fontWeight: 600 }}>Sin historial de copas</p>
              <span style={{ fontSize: '0.82rem', color: '#71717a' }}>Las gráficas aparecerán cuando se registren puntajes en copas</span>
            </div>
          )}

          {!scoresLoading && historicScores.length > 0 && (
            <>
              {/* Evolución del promedio (línea temporal) */}
              {temporalSeries.length >= 2 && (
                <div className="cd-analysis-chart-card" style={{ marginBottom: 16, padding: 12, background: '#fafafa', borderRadius: 10, border: '1px solid #e4e4e7' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, color: '#3f3f46', fontWeight: 600, fontSize: '0.88rem' }}>
                    <TrendingUp size={16} style={{ color: '#8b5cf6' }} /> Evolución por Copa
                  </div>
                  <ResponsiveContainer width="100%" height={230}>
                    <LineChart data={temporalSeries}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="dateLabel" tick={{ fontSize: 10 }} />
                      <YAxis domain={[0, 10]} tick={{ fontSize: 10 }} />
                      <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #eee', fontSize: 12 }}
                        labelFormatter={(_, payload) => payload?.[0]?.payload?.name || ''} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="average" stroke="#8b5cf6" strokeWidth={3} name="Promedio" dot={{ fill: '#8b5cf6', r: 4 }} />
                      <Line type="monotone" dataKey="total" stroke="#10b981" strokeWidth={2} name="Total" dot={{ fill: '#10b981', r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Promedio/máximo por aparato (barras) */}
              {apparatusAgg.length > 0 && (
                <div className="cd-analysis-chart-card" style={{ marginBottom: 16, padding: 12, background: '#fafafa', borderRadius: 10, border: '1px solid #e4e4e7' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, color: '#3f3f46', fontWeight: 600, fontSize: '0.88rem' }}>
                    <BarChart3 size={16} style={{ color: '#3b82f6' }} /> Rendimiento por Aparato
                  </div>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={apparatusAgg} barSize={18}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="apparatus" tick={{ fontSize: 10 }} />
                      <YAxis domain={[0, 10]} tick={{ fontSize: 10 }} />
                      <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #eee', fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="avg" fill="#8b5cf6" name="Promedio" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="max" fill="#10b981" name="Máximo" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Radar de perfil */}
              {apparatusAgg.length >= 3 && (
                <div className="cd-analysis-chart-card" style={{ marginBottom: 16, padding: 12, background: '#fafafa', borderRadius: 10, border: '1px solid #e4e4e7' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, color: '#3f3f46', fontWeight: 600, fontSize: '0.88rem' }}>
                    <Target size={16} style={{ color: '#ec4899' }} /> Perfil de Rendimiento
                  </div>
                  <ResponsiveContainer width="100%" height={260}>
                    <RadarChart data={apparatusAgg}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="apparatus" tick={{ fontSize: 10 }} />
                      <PolarRadiusAxis domain={[0, 10]} tick={{ fontSize: 9 }} />
                      <Radar name="Promedio" dataKey="avg" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.25} />
                      <Radar name="Máximo" dataKey="max" stroke="#10b981" fill="#10b981" fillOpacity={0.15} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #eee', fontSize: 12 }} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Evolución por aparato (multi-línea) */}
              {apparatusEvolution.apparatusKeys.length > 0 && apparatusEvolution.data.length >= 2 && (
                <div className="cd-analysis-chart-card" style={{ marginBottom: 16, padding: 12, background: '#fafafa', borderRadius: 10, border: '1px solid #e4e4e7' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, color: '#3f3f46', fontWeight: 600, fontSize: '0.88rem' }}>
                    <Activity size={16} style={{ color: '#14b8a6' }} /> Evolución por Aparato
                  </div>
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={apparatusEvolution.data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="dateLabel" tick={{ fontSize: 10 }} />
                      <YAxis domain={[0, 10]} tick={{ fontSize: 10 }} />
                      <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #eee', fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                      {apparatusEvolution.apparatusKeys.slice(0, 8).map((k, i) => (
                        <Line key={k} type="monotone" dataKey={k} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Detalle por copa (tabla compacta) */}
              <div className="cd-analysis-chart-card" style={{ marginBottom: 16, padding: 12, background: '#fafafa', borderRadius: 10, border: '1px solid #e4e4e7' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, color: '#3f3f46', fontWeight: 600, fontSize: '0.88rem' }}>
                  <Calendar size={16} style={{ color: '#f59e0b' }} /> Detalle por Copa
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table className="cd-table" style={{ width: '100%', minWidth: 0, fontSize: '0.78rem' }}>
                    <thead>
                      <tr>
                        <th>Copa</th>
                        <th>Fecha</th>
                        <th style={{ textAlign: 'center' }}>Prom.</th>
                        <th style={{ textAlign: 'center' }}>Total</th>
                        <th style={{ textAlign: 'center' }}>Δ Prom</th>
                      </tr>
                    </thead>
                    <tbody>
                      {temporalSeries.map((s, i) => {
                        const prev = i > 0 ? temporalSeries[i - 1] : null
                        const delta = prev ? parseFloat((s.average - prev.average).toFixed(2)) : null
                        return (
                          <tr key={s.id}>
                            <td><strong>{s.name}</strong></td>
                            <td>{s.dateLabel}</td>
                            <td style={{ textAlign: 'center' }}>
                              <span className={`cd-score-pill ${s.average >= 8 ? 'high' : s.average >= 6 ? 'mid' : ''}`}>{s.average}</span>
                            </td>
                            <td style={{ textAlign: 'center' }}>{s.total}</td>
                            <td style={{ textAlign: 'center' }}>
                              {delta === null ? <span style={{ color: '#cbd5e1' }}>—</span> : (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: delta > 0 ? '#16a34a' : delta < 0 ? '#dc2626' : '#64748b', fontWeight: 600 }}>
                                  {delta > 0 ? <TrendingUp size={11} /> : delta < 0 ? <TrendingDown size={11} /> : null}
                                  {delta > 0 ? '+' : ''}{delta}
                                </span>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* ===== Sección IA ===== */}
          {loading && (
            <div className="cd-loading-state">
              <div className="cd-spinner" />
              <p>Analizando con IA...</p>
            </div>
          )}

          {error && !loading && (
            <div style={{
              padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: 12,
              background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, marginTop: 8
            }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <AlertTriangle size={16} style={{ color: '#d97706' }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: '0.82rem', color: '#92400e', fontWeight: 600 }}>Análisis IA no disponible</p>
                <p style={{ margin: '4px 0 10px', fontSize: '0.78rem', color: '#78350f', lineHeight: 1.45 }}>{error}</p>
                <button
                  onClick={refetch}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: '#f59e0b', color: 'white', border: 'none',
                    padding: '6px 12px', borderRadius: 6, fontSize: '0.76rem',
                    fontWeight: 600, cursor: 'pointer'
                  }}
                >
                  <Activity size={13} /> Reintentar
                </button>
              </div>
            </div>
          )}

          {analysis && !loading && (
            <div className="cd-analysis-content">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0 12px', color: '#8b5cf6', fontWeight: 700, fontSize: '0.95rem' }}>
                <Star size={18} /> Conclusiones de la IA
                <span style={{ fontSize: '0.72rem', background: '#f5f3ff', color: '#7c3aed', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>
                  {analysis.years_in_club.toFixed(1)} años en el club
                </span>
              </div>

              <div className="cd-analysis-summary">
                <p>{analysis.summary}</p>
              </div>

              <div className="cd-analysis-grid">
                <div className="cd-analysis-card">
                  <div className="cd-card-title"><Star size={16} /> Fortalezas</div>
                  <ul className="cd-analysis-list">
                    {analysis.strengths?.map((s, i) => (
                      <li key={i}><Award size={14} className="icon-green" /> {s}</li>
                    ))}
                  </ul>
                </div>

                <div className="cd-analysis-card">
                  <div className="cd-card-title"><Target size={16} /> Áreas a Mejorar</div>
                  <ul className="cd-analysis-list">
                    {analysis.weaknesses?.map((w, i) => (
                      <li key={i}><Target size={14} className="icon-orange" /> {w}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="cd-analysis-insights">
                <div className="cd-insight-item">
                  <Activity size={16} />
                  <div>
                    <strong>Consistencia</strong>
                    <p>{analysis.consistency_analysis}</p>
                  </div>
                </div>

                <div className="cd-insight-item">
                  <TrendingUp size={16} />
                  <div>
                    <strong>Tendencia</strong>
                    <p>{analysis.trend_analysis}</p>
                  </div>
                </div>
              </div>

              {analysis.risk_flags?.length > 0 && (
                <div className="cd-risk-section">
                  <div className="cd-card-title"><AlertTriangle size={16} /> Flags de Riesgo</div>
                  <div className="cd-risk-tags">
                    {analysis.risk_flags.map((flag, i) => (
                      <span key={i} className="cd-risk-tag">{flag}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="cd-recommendations-section">
                <div className="cd-card-title"><Target size={16} /> Recomendaciones</div>
                <ul className="cd-recommendations-list">
                  {analysis.recommendations?.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default GymnastAnalysisModal